/**
 * RCA Scoring Engine
 * Project Risk & Complexity Assessment
 *
 * Pure-function scoring engine with no global state dependencies.
 * All functions accept their required data as parameters and return
 * immutable result objects.
 *
 * Scoring math is identical to the DMS-F-0505A implementation:
 *   raw = sum(answer_multiplier * question_weight * 20 / totalWeight)
 *         / sum(max_multiplier * question_weight * 20 / totalWeight) * 100
 *   mapped = 100 + raw * 4   (yields a 100-500 scale)
 *
 * @version 2.0.0
 */

import type {
  Phase,
  PhaseId,
  SectionType,
  AnswerValue,
  PhaseAnswers,
  ScoreResult,
  RiskLevel,
  RCACategory,
  PhaseCategory,
} from '../types/rca';

// ============================================================
// Score thresholds — DMS-F-0505A
// ============================================================

const SCORE_HIGH_THRESHOLD = 368;
const SCORE_MEDIUM_THRESHOLD = 234;

// ============================================================
// Phase order for cumulative calculations
// ============================================================

const PHASE_ORDER: PhaseId[] = ['pre_charter', 'initiation', 'planning', 'event_driven'];

// ============================================================
// CATEGORY LOOKUP — Risk × Complexity → Category 1-4
// ============================================================

const CATEGORY_LOOKUP: Record<string, RCACategory> = {
  'High_High': 4,
  'High_Medium': 3,
  'High_Low': 3,
  'Medium_High': 3,
  'Medium_Medium': 2,
  'Medium_Low': 2,
  'Low_High': 2,
  'Low_Medium': 1,
  'Low_Low': 1,
};

// ============================================================
// computeRawScore
// ============================================================

/**
 * Compute the raw and mapped score for a single phase + section type.
 *
 * Formula:
 *   For each answered (non-NA) question:
 *     totalScore += ans.m * q.weight * 20 / totalWeight
 *     maxPossible += maxM * q.weight * 20 / totalWeight
 *   raw = (totalScore / maxPossible) * 100
 *   mapped = 100 + raw * 4
 *
 * @param phaseId   - Phase key (e.g. 'pre_charter')
 * @param section   - 'risk' or 'complexity'
 * @param answers   - Answer map keyed by "phaseId_section_questionNum"
 * @param phases    - Full phases array for question lookup
 * @returns ScoreResult or null if no questions or no scorable answers
 */
export function computeRawScore(
  phaseId: PhaseId,
  section: SectionType,
  answers: PhaseAnswers,
  phases: Phase[]
): ScoreResult | null {
  const phase = phases.find((p) => p.id === phaseId);
  const sectionData = phase ? phase[section] : null;
  if (!sectionData) return null;

  const tw = sectionData.totalWeight;
  let totalScore = 0;
  let maxPossible = 0;
  let allAnswered = true;
  let naCount = 0;
  let answeredCount = 0;
  const totalQuestions = sectionData.questions.length;

  for (const q of sectionData.questions) {
    const key = `${phaseId}_${section}_${q.num}`;
    const sel: AnswerValue | undefined = answers[key];

    if (sel === undefined || sel === null) {
      allAnswered = false;
      continue;
    }

    if (sel === 'NA') {
      naCount++;
      answeredCount++;
      continue;
    }

    answeredCount++;
    const ans = q.answers[sel as number];
    if (!ans) continue;

    // Find the maximum multiplier among this question's answers
    const maxM = q.answers.reduce((max, a) => (a.m > max ? a.m : max), 0);

    totalScore += ans.m * q.weight * 20 / tw;
    maxPossible += maxM * q.weight * 20 / tw;
  }

  if (maxPossible === 0) return null;

  const normalized = (totalScore / maxPossible) * 100;

  return {
    raw: normalized,
    mapped: 100 + normalized * 4,
    allAnswered,
    naCount,
    answeredCount,
    totalQuestions,
  };
}

// ============================================================
// classifyScore
// ============================================================

/**
 * Classify a mapped score (100-500) into a risk/complexity level.
 *
 * @param mapped - The mapped score, or null/undefined
 * @returns 'High', 'Medium', 'Low', or 'Not Scored'
 */
export function classifyScore(mapped: number | null | undefined): RiskLevel {
  if (mapped === null || mapped === undefined) return 'Not Scored';
  if (mapped >= SCORE_HIGH_THRESHOLD) return 'High';
  if (mapped >= SCORE_MEDIUM_THRESHOLD) return 'Medium';
  return 'Low';
}

// ============================================================
// getCategoryFromLevels
// ============================================================

/**
 * Maps risk × complexity levels to a DMS category (1-4).
 *
 * @param riskLevel       - 'High' | 'Medium' | 'Low' | 'Not Scored'
 * @param complexityLevel - 'High' | 'Medium' | 'Low' | 'Not Scored'
 * @returns Category number 1-4, or null if either level is 'Not Scored'
 */
export function getCategoryFromLevels(
  riskLevel: RiskLevel,
  complexityLevel: RiskLevel
): RCACategory | null {
  if (riskLevel === 'Not Scored' || complexityLevel === 'Not Scored') return null;
  const key = `${riskLevel}_${complexityLevel}`;
  return CATEGORY_LOOKUP[key] ?? null;
}

// ============================================================
// computePhaseCategory
// ============================================================

/**
 * Compute the cumulative category for a given phase by averaging
 * all completed phase scores up to and including this phase.
 *
 * Event-driven phase uses only non-event-driven phases for complexity
 * (because event_driven has no complexity section of its own).
 *
 * @param phaseId      - The phase to compute up to
 * @param activePhases - Array of active phase IDs
 * @param answers      - Full answer map
 * @param phases       - Full phases array
 * @returns PhaseCategory with averaged scores and classification
 */
export function computePhaseCategory(
  phaseId: PhaseId,
  activePhases: PhaseId[],
  answers: PhaseAnswers,
  phases: Phase[]
): PhaseCategory {
  const idx = PHASE_ORDER.indexOf(phaseId);
  const activeUpTo: PhaseId[] = [];

  for (let i = 0; i <= idx; i++) {
    const p = PHASE_ORDER[i];
    if (activePhases.includes(p)) {
      activeUpTo.push(p);
    }
  }

  // Average risk scores across all active phases up to this one
  const riskScores: number[] = [];
  for (const p of activeUpTo) {
    const s = computeRawScore(p, 'risk', answers, phases);
    if (s && s.allAnswered) riskScores.push(s.mapped);
  }
  const avgRisk = riskScores.length > 0
    ? riskScores.reduce((a, b) => a + b, 0) / riskScores.length
    : null;

  // Average complexity scores — event_driven has no complexity section,
  // so exclude it from the complexity phase list
  const complexityPhases: PhaseId[] = phaseId === 'event_driven'
    ? activeUpTo.filter((p) => p !== 'event_driven')
    : activeUpTo;

  const complexityScores: number[] = [];
  for (const p of complexityPhases) {
    const cs = computeRawScore(p, 'complexity', answers, phases);
    if (cs && cs.allAnswered) complexityScores.push(cs.mapped);
  }
  const avgComplexity = complexityScores.length > 0
    ? complexityScores.reduce((a, b) => a + b, 0) / complexityScores.length
    : null;

  const riskLevel = classifyScore(avgRisk);
  const complexityLevel = classifyScore(avgComplexity);
  const category = getCategoryFromLevels(riskLevel, complexityLevel);

  return {
    avgRisk,
    avgComplexity,
    riskLevel,
    complexityLevel,
    category,
  };
}

// ============================================================
// computeOverall
// ============================================================

/**
 * Compute overall risk, complexity, and category across all active phases.
 *
 * Risk: averages all completed phase risk scores.
 * Complexity: averages all completed non-event-driven complexity scores.
 * Category: looked up from the Risk × Complexity matrix.
 *
 * @param activePhases - Array of active phase IDs
 * @param answers      - Full answer map
 * @param phases       - Full phases array
 * @returns PhaseCategory with overall averaged scores
 */
export function computeOverall(
  activePhases: PhaseId[],
  answers: PhaseAnswers,
  phases: Phase[]
): PhaseCategory {
  const allRisk: number[] = [];
  const allComplexity: number[] = [];

  for (const p of activePhases) {
    const s = computeRawScore(p, 'risk', answers, phases);
    if (s && s.allAnswered) allRisk.push(s.mapped);

    // event_driven has no complexity section — skip it
    if (p !== 'event_driven') {
      const cs = computeRawScore(p, 'complexity', answers, phases);
      if (cs && cs.allAnswered) allComplexity.push(cs.mapped);
    }
  }

  const avgRisk = allRisk.length > 0
    ? allRisk.reduce((a, b) => a + b, 0) / allRisk.length
    : null;
  const avgComplexity = allComplexity.length > 0
    ? allComplexity.reduce((a, b) => a + b, 0) / allComplexity.length
    : null;

  const riskLevel = classifyScore(avgRisk);
  const complexityLevel = classifyScore(avgComplexity);
  const category = getCategoryFromLevels(riskLevel, complexityLevel);

  return {
    avgRisk,
    avgComplexity,
    riskLevel,
    complexityLevel,
    category,
  };
}
